import React, { useEffect, useState } from "react";
import { Loader2, Wallet, Send, Eye, EyeOff, Copy, Check, AlertTriangle, ShieldCheck, ExternalLink, Fuel } from "lucide-react";
import { toast } from "sonner";
import * as bip39 from "bip39";
import { derivePath } from "ed25519-hd-key";
import { Keypair } from "@solana/web3.js";
import {
  getAegisSplConfig,
  getAegisSplWallet,
  linkSolanaWallet,
  withdrawAegisToSolana,
  getAegisSplAuthorityBalance,
} from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Solana withdraw card · in-browser keypair generation + on-chain mint.
 *
 * Flow:
 *  1. Show current linked Solana wallet (if any) + on-chain balance.
 *  2. If no wallet linked, expose "Generate wallet" — creates a BIP-39
 *     mnemonic + Phantom-compatible Solana keypair entirely in-browser.
 *     The seed phrase is shown ONCE; user must check "I saved it".
 *  3. After link, "Withdraw" input + button burns N off-chain AEGIS and
 *     mints N on-chain AEGIS to the linked address.
 *
 * Security:
 *  - Browser-only seed generation — server never sees the secret.
 *  - Public key sent to server via POST /aegis/spl/link-wallet.
 *  - Locked vault coins never auto-withdrawn.
 */
export default function SolanaWithdrawCard({ wallet, onSent }) {
  const { user } = useAuth();
  const isArchitect = !!user?.is_architect;
  const [cfg, setCfg] = useState(null);
  const [link, setLink] = useState(null);
  const [authBal, setAuthBal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // {pubkey, mnemonic}
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [savedConfirm, setSavedConfirm] = useState(false);
  const [manualAddr, setManualAddr] = useState("");
  const [amount, setAmount] = useState("1");
  const [submitting, setSubmitting] = useState(false);
  const [lastTx, setLastTx] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        getAegisSplConfig(),
        getAegisSplWallet().catch(() => ({ linked: false, on_chain_balance: 0 })),
      ]);
      setCfg(c);
      setLink(w);
      if (isArchitect) {
        getAegisSplAuthorityBalance().then(setAuthBal).catch(() => {});
      }
    } catch (e) {
      toast.error("Could not load Solana bridge state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast.success("Copied to clipboard");
  };

  const generateWallet = async () => {
    setGenerating(true);
    try {
      const mnemonic = bip39.generateMnemonic(128); // 12 words
      const seed = await bip39.mnemonicToSeed(mnemonic);
      const seedHex = seed.toString("hex");
      // Phantom-compatible derivation path
      const derived = derivePath("m/44'/501'/0'/0'", seedHex).key;
      const kp = Keypair.fromSeed(derived);
      const pubkey = kp.publicKey.toBase58();
      setGenerated({ pubkey, mnemonic });
      setShowMnemonic(false);
      setSavedConfirm(false);
    } catch (e) {
      toast.error("Wallet generation failed");
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const confirmAndLink = async () => {
    if (!savedConfirm) {
      toast.error("Please confirm you saved the recovery phrase first.");
      return;
    }
    try {
      await linkSolanaWallet(generated.pubkey);
      toast.success("External Solana wallet linked.");
      setGenerated(null);
      setShowMnemonic(false);
      setSavedConfirm(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link wallet");
    }
  };

  const linkManual = async () => {
    const addr = manualAddr.trim();
    if (!addr) return;
    try {
      await linkSolanaWallet(addr);
      toast.success("Solana wallet linked.");
      setManualAddr("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link wallet");
    }
  };

  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      const r = await withdrawAegisToSolana(amt);
      setLastTx(r);
      toast.success(`Withdrew ${amt} AEGIS on-chain`);
      onSent && onSent();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdrawal failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-zinc-900 bg-zinc-950 p-4 flex items-center gap-3 text-xs text-zinc-500" data-testid="solana-withdraw-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Solana bridge…
      </div>
    );
  }

  if (!cfg?.configured) {
    return (
      <div className="border border-zinc-900 bg-zinc-950 p-4 text-xs text-zinc-500" data-testid="solana-withdraw-unconfigured">
        <div className="flex items-center gap-2 text-amber-300 mb-1">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span className="uppercase tracking-[0.25em] text-[10px]">Solana bridge offline</span>
        </div>
        The Aegis Token SPL bridge is not configured on this server yet.
      </div>
    );
  }

  const tradeable = wallet?.tradeable_count ?? wallet?.count ?? 0;
  // Vesting cliff — verifier-payout coins still inside their cliff
  // window count as tradeable (P2P-transferable) but NOT withdrawable
  // to Solana. Fall back to tradeable_apt when the backend hasn't
  // populated the withdrawable fields yet (older cached response).
  const withdrawableAptRaw = wallet?.withdrawable_apt ?? wallet?.tradeable_apt ?? 0;
  const withdrawableApt = Math.floor(withdrawableAptRaw);
  const vestingLockedCount = wallet?.vesting_locked_count ?? 0;
  const vestingLockedApt = wallet?.vesting_locked_apt ?? 0;
  const vestingCliffDays = wallet?.vesting_cliff_days ?? 60;
  const nextVestingUnlock = wallet?.next_vesting_unlock;
  const nextVestingCountdown = (() => {
    if (!nextVestingUnlock) return null;
    const target = new Date(nextVestingUnlock).getTime();
    if (!Number.isFinite(target)) return null;
    const ms = target - Date.now();
    if (ms <= 0) return null;
    const days = Math.floor(ms / 86_400_000);
    if (days >= 2) return `${days} days`;
    if (days === 1) return "1 day";
    const hrs = Math.floor(ms / 3_600_000);
    return hrs >= 1 ? `${hrs} hr${hrs === 1 ? "" : "s"}` : "under an hour";
  })();

  return (
    <div className="border border-amber-500/30 bg-gradient-to-br from-zinc-950 to-black p-5" data-testid="solana-withdraw-card">
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-1 inline-flex items-center gap-2">
            <Wallet className="h-3 w-3" /> Withdraw to your external wallet · Solana
          </div>
          <div className="text-zinc-400 text-xs leading-relaxed max-w-xl">
            Move your AEGIS out of Projectivy and into a Phantom-compatible wallet
            you fully own. The wallet is generated in your browser — we never see
            your secret recovery phrase.
          </div>
        </div>
        <div className="text-right text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
          <div>mint · <span className="text-amber-300">{cfg.symbol}</span></div>
          <div>net · <span className="text-zinc-300">{cfg.network}</span></div>
        </div>
      </div>

      {/* Architect-only — low SOL nag for the mint authority hot wallet */}
      {isArchitect && authBal && authBal.configured && authBal.status !== "ok" && (
        <div
          className={`mb-3 border ${authBal.status === "critical" ? "border-rose-500/50 bg-rose-500/10 text-rose-200" : "border-amber-500/50 bg-amber-500/10 text-amber-200"} p-3 text-xs`}
          data-testid="spl-low-balance-banner"
        >
          <div className="flex items-center gap-2 mb-1 uppercase tracking-[0.25em] text-[10px]">
            <Fuel className="h-3 w-3" />
            {authBal.status === "critical" ? "critical · authority wallet almost empty" : "low · authority wallet draining"}
          </div>
          <div className="text-zinc-300 leading-relaxed">
            <span className="font-mono">{authBal.sol.toFixed(4)} SOL</span> remaining
            (~<span className="font-mono">{authBal.new_recipients_remaining_estimate}</span>{" "}
            new recipients covered before withdrawals start failing). Send SOL to{" "}
            <code className="font-mono text-zinc-100 break-all">{authBal.authority_pub}</code>{" "}
            on <span className="text-amber-300">{authBal.network}</span> to refill.
          </div>
        </div>
      )}

      {/* State: not linked yet → show generator + manual entry */}
      {!link?.linked && !generated && (
        <div className="space-y-3" data-testid="solana-not-linked">
          <div className="border border-zinc-900 bg-zinc-950 p-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-2">Option A · Generate a fresh wallet</div>
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              Creates a new Phantom-compatible Solana wallet right here in your
              browser. You&apos;ll see a 12-word recovery phrase ONCE — write it down
              somewhere safe. Anyone with that phrase owns the wallet forever.
            </p>
            <button
              onClick={generateWallet}
              disabled={generating}
              data-testid="solana-generate-wallet"
              className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 transition-colors text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
            >
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wallet className="h-3.5 w-3.5" />}
              {generating ? "Generating…" : "Generate wallet"}
            </button>
          </div>
          <div className="border border-zinc-900 bg-zinc-950 p-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-2">Option B · Use my existing Phantom address</div>
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              Already have a Phantom or Solflare wallet? Paste the public address.
            </p>
            <div className="flex gap-2">
              <input
                value={manualAddr}
                onChange={(e) => setManualAddr(e.target.value)}
                placeholder="paste your Solana public address (base58)"
                data-testid="solana-manual-address"
                className="flex-1 bg-black border border-zinc-800 px-3 py-2 text-xs font-mono text-zinc-100 placeholder-zinc-700 focus:outline-none focus:border-amber-500/50"
              />
              <button
                onClick={linkManual}
                disabled={!manualAddr.trim()}
                data-testid="solana-link-manual"
                className="px-4 py-2 border border-zinc-700 text-zinc-200 hover:border-amber-500/60 hover:text-amber-300 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
              >
                Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* State: just generated → reveal mnemonic */}
      {generated && (
        <div className="border border-rose-500/40 bg-rose-500/5 p-4 space-y-3" data-testid="solana-mnemonic-reveal">
          <div className="flex items-center gap-2 text-rose-300 text-[11px] uppercase tracking-[0.25em]">
            <AlertTriangle className="h-3.5 w-3.5" />
            Critical · save this recovery phrase NOW
          </div>
          <div className="text-xs text-zinc-300 leading-relaxed">
            Write these 12 words on paper and store them somewhere safe. Anyone with this phrase
            controls the wallet. Projectivy can&apos;t recover it for you.
          </div>
          <div className="border border-zinc-800 bg-black p-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Recovery phrase</span>
              <button
                onClick={() => setShowMnemonic((s) => !s)}
                data-testid="solana-mnemonic-toggle"
                className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-amber-300 uppercase tracking-[0.2em]"
              >
                {showMnemonic ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {showMnemonic ? "Hide" : "Reveal"}
              </button>
            </div>
            {showMnemonic ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                {generated.mnemonic.split(" ").map((w, i) => (
                  <div key={i} className="bg-zinc-950 border border-zinc-900 px-2 py-1.5 text-[11px] font-mono text-zinc-200" data-testid={`mnemonic-word-${i}`}>
                    <span className="text-zinc-600 mr-1">{i + 1}.</span>{w}
                  </div>
                ))}
              </div>
            ) : (
              <div className="font-mono text-zinc-700 text-xs select-none">
                •••• •••• •••• •••• •••• •••• •••• •••• •••• •••• •••• ••••
              </div>
            )}
            {showMnemonic && (
              <button
                onClick={() => copy(generated.mnemonic, "mnemonic")}
                className="mt-2 inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-amber-300 uppercase tracking-[0.2em]"
                data-testid="solana-copy-mnemonic"
              >
                {copiedKey === "mnemonic" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copiedKey === "mnemonic" ? "Copied" : "Copy phrase"}
              </button>
            )}
          </div>
          <div className="border border-zinc-800 bg-black p-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Public address</div>
            <div className="flex items-center gap-2">
              <code className="text-[11px] font-mono text-zinc-200 break-all" data-testid="solana-generated-pubkey">{generated.pubkey}</code>
              <button onClick={() => copy(generated.pubkey, "pubkey")} className="text-zinc-500 hover:text-amber-300">
                {copiedKey === "pubkey" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="solana-saved-confirm-label">
            <input
              type="checkbox"
              checked={savedConfirm}
              onChange={(e) => setSavedConfirm(e.target.checked)}
              data-testid="solana-saved-confirm"
              className="mt-0.5"
            />
            I&apos;ve saved the 12-word recovery phrase somewhere safe. I understand
            Projectivy cannot recover it for me if I lose it.
          </label>
          <div className="flex gap-2 pt-1">
            <button
              onClick={confirmAndLink}
              disabled={!savedConfirm}
              data-testid="solana-confirm-link"
              className="px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
            >
              <ShieldCheck className="h-3.5 w-3.5 inline mr-1.5" />
              Link this wallet to my account
            </button>
            <button
              onClick={() => { setGenerated(null); setShowMnemonic(false); setSavedConfirm(false); }}
              data-testid="solana-cancel-generation"
              className="px-4 py-2 border border-zinc-800 text-zinc-400 hover:border-zinc-600 text-[11px] uppercase tracking-[0.25em]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* State: linked → show withdraw form */}
      {link?.linked && (
        <div className="space-y-3" data-testid="solana-linked">
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="border border-zinc-900 bg-zinc-950 p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Linked external wallet</div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] font-mono text-zinc-200 break-all" data-testid="solana-linked-address">{link.solana_wallet}</code>
                <button onClick={() => copy(link.solana_wallet, "linked")} className="text-zinc-500 hover:text-amber-300 shrink-0">
                  {copiedKey === "linked" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </button>
              </div>
            </div>
            <div className="border border-zinc-900 bg-zinc-950 p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">On-chain balance</div>
              <div className="text-2xl font-light text-amber-300" data-testid="solana-onchain-balance">
                {link.on_chain_balance ?? 0} <span className="text-xs text-zinc-500 ml-1">AEGIS</span>
              </div>
            </div>
          </div>

          <div className="border border-amber-500/20 bg-amber-500/5 p-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-2 inline-flex items-center gap-2">
              <Send className="h-3 w-3" /> Withdraw AEGIS on-chain
            </div>
            {vestingLockedCount > 0 && (
              <div
                className="mb-3 border border-sky-500/40 bg-sky-500/5 text-sky-200 p-2 text-[11px] leading-relaxed"
                data-testid="solana-vesting-notice"
              >
                <div className="uppercase tracking-[0.25em] text-[10px] text-sky-300 mb-1">
                  ◷ Vesting cliff · {vestingCliffDays}-day
                </div>
                <div className="font-mono">
                  {vestingLockedCount} verifier-payout coin{vestingLockedCount === 1 ? "" : "s"}
                  {" "}(<span className="text-sky-100">{Number(vestingLockedApt).toFixed(2)} APT</span>)
                  {" "}still inside the cliff.
                  {nextVestingCountdown && (
                    <> Next unlock in <span className="text-sky-100">{nextVestingCountdown}</span>.</>
                  )}
                  {" "}They stay in your wallet and P2P transfer works — only the SPL withdraw is gated.
                </div>
              </div>
            )}
            <div className="flex gap-2 items-end flex-wrap">
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-1">
                  Amount · whole APT
                </label>
                <input
                  type="number"
                  min="1"
                  max={Math.min(1000, withdrawableApt) || 1}
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="solana-withdraw-amount"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm font-mono text-zinc-100 focus:outline-none focus:border-amber-500/50"
                />
                <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                  Withdrawable · <span className="text-zinc-300 font-mono">{Number(withdrawableAptRaw).toFixed(2)}</span> APT cleared
                  <span className="text-zinc-700"> · </span>
                  <span className="text-zinc-600">across {tradeable} coin{tradeable === 1 ? "" : "s"}</span>
                  <br />
                  Whole APT only (mint decimals · 0). Dust beyond the requested amount is burned as the cost of withdrawing.
                </div>
              </div>
              <button
                onClick={submit}
                disabled={submitting || !amount}
                data-testid="solana-withdraw-submit"
                className="px-5 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.25em] disabled:opacity-40"
              >
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" /> : <Send className="h-3.5 w-3.5 inline mr-1.5" />}
                {submitting ? "Withdrawing…" : "Withdraw"}
              </button>
            </div>
          </div>

          {lastTx && (
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-3 text-xs text-emerald-300" data-testid="solana-withdraw-success">
              <div className="flex items-center gap-2 mb-1 uppercase tracking-[0.25em] text-[10px]">
                <ShieldCheck className="h-3 w-3" /> on-chain mint confirmed
              </div>
              <div className="text-zinc-300 break-all">
                <span className="text-amber-300 font-mono">{lastTx.amount}</span> AEGIS sent to{" "}
                <span className="font-mono text-zinc-100">{lastTx.to_wallet}</span>
                {lastTx.coin_count != null && (
                  <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                    Burned {lastTx.coin_count} off-chain coin{lastTx.coin_count === 1 ? "" : "s"} worth{" "}
                    <span className="font-mono text-zinc-400">{Number(lastTx.coin_value_apt_sum || 0).toFixed(2)}</span> APT
                    {lastTx.excess_apt_burned > 0 && (
                      <>
                        {" "}· <span className="text-rose-300">{Number(lastTx.excess_apt_burned).toFixed(2)} APT dust burned</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <a
                href={lastTx.explorer_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
                data-testid="solana-withdraw-explorer-link"
              >
                <ExternalLink className="h-3 w-3" /> view on Solscan
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
